import { resolveClarityProjectId } from '@/analytics/analytics-runtime-config';
import {
  onFirstPageInteraction,
  readHasFirstPageInteraction,
} from '@/lib/ui/first-page-interaction';

declare global {
  interface Window {
    clarity?: AnalyticsClarityFn & { q?: unknown[][]; v?: string };
  }
}

type AnalyticsClarityFn = (...args: unknown[]) => unknown;

const CLARITY_SCRIPT_ID = 'analytics-clarity-script';

let clarityInitPromise: Promise<void> | null = null;
let clarityBootstrapListening = false;
let clarityReady = false;
let clarityUserId: string | null | undefined;
let identifiedUserId: string | null = null;
let identityUpdate = Promise.resolve();

function isBrowser() {
  return typeof window !== 'undefined';
}

function isDevelopment() {
  return process.env.NODE_ENV === 'development';
}

function loadExternalScript(params: { id: string; src: string }) {
  if (!isBrowser()) {
    return Promise.resolve();
  }

  const existingScript = document.getElementById(params.id);
  if (existingScript) {
    return Promise.resolve();
  }

  return new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.id = params.id;
    script.async = true;
    script.src = params.src;
    script.onload = () => resolve();
    script.onerror = () => {
      script.remove();
      reject(new Error(`Failed to load ${params.src}`));
    };
    document.head.appendChild(script);
  });
}

function ensureClarityStub() {
  if (!isBrowser()) {
    return;
  }

  window.clarity =
    window.clarity ||
    function clarity(...args: unknown[]) {
      const queued = window.clarity?.q || [];
      queued.push(args);
      window.clarity = Object.assign(clarity, { q: queued });
    };
}

async function initializeClarity(projectId: string) {
  ensureClarityStub();
  await loadExternalScript({
    id: CLARITY_SCRIPT_ID,
    src: `https://www.clarity.ms/tag/${encodeURIComponent(projectId)}`,
  });
  window.clarity?.(
    'metadata',
    () => {
      clarityReady = true;
      void syncClarityUser(clarityUserId);
    },
    false
  );
}

async function initializeClarityBootstrap() {
  if (!isBrowser()) {
    return;
  }

  const clarityProjectId = resolveClarityProjectId();
  if (!clarityProjectId) {
    clarityReady = false;
    return;
  }

  if (clarityInitPromise) {
    return clarityInitPromise;
  }

  clarityInitPromise = initializeClarity(clarityProjectId).catch((error) => {
    clarityInitPromise = null;

    if (isDevelopment()) {
      console.error('[clarity-browser] Failed to initialize Clarity', error);
    }
  });

  return clarityInitPromise;
}

function scheduleClarityBootstrap() {
  const clarityProjectId = resolveClarityProjectId();
  if (!isBrowser() || !clarityProjectId) {
    return;
  }

  if (!readHasFirstPageInteraction()) {
    if (clarityBootstrapListening) {
      return;
    }

    clarityBootstrapListening = true;
    onFirstPageInteraction(() => {
      clarityBootstrapListening = false;
      void initializeClarityBootstrap();
    });
    return;
  }

  clarityBootstrapListening = false;
  void initializeClarityBootstrap();
}

export function startClarityBootstrap() {
  if (!isBrowser()) {
    return;
  }

  scheduleClarityBootstrap();
}

export function syncClarityUser(userId: string | null | undefined) {
  if (!isBrowser() || !resolveClarityProjectId()) {
    return;
  }

  // Keep only the current identity until the SDK can return its identify Promise.
  clarityUserId = userId === undefined ? undefined : userId?.trim() || null;
  if (!clarityReady || clarityUserId === undefined) return;

  const nextUserId = clarityUserId;
  identityUpdate = identityUpdate
    .then(async () => {
      if (nextUserId !== clarityUserId) return;
      const identityChanged =
        identifiedUserId && identifiedUserId !== nextUserId;
      if (identityChanged && window.clarity?.v) {
        // Official stop/start clears custom variables; identify hashes asynchronously.
        // https://github.com/microsoft/clarity/blob/8aa987e469a1fe11dbf984ffc98f3344468e4af4/packages/clarity-js/src/data/variable.ts
        window.clarity?.('stop');
      }
      // start is a no-op when active and resumes the SDK's temporary SPA queue.
      if (identityChanged || nextUserId) {
        window.clarity?.('start');
      }
      identifiedUserId = nextUserId;
      if (nextUserId) await window.clarity?.('identify', nextUserId);
    })
    .catch((error) => {
      console.error(
        '[clarity-browser] Failed to synchronize user identity',
        error
      );
    });
  return identityUpdate;
}

export function dispatchClarityEvent(event: string) {
  if (!isBrowser() || !resolveClarityProjectId() || !clarityReady) {
    return;
  }

  window.clarity?.('event', event);
}
